import React, { useContext } from "react";
import { Link } from "react-router-dom";

import HealthBadge from '../HealthBadge';

import AvailabilityGroup from '../../../types/cluster/AvailabilityGroup';
import AvailabilityReplica from '../../../types/cluster/AvailabilityReplica';
import AvailabilityListener from '../../../types/cluster/AvailabilityListener';

import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildRedirectQuery } from "../../../helpers/timeRangeUtils";

interface AvailabilityGroupsTableProps {
    clusterId: number,
    groups: AvailabilityGroup[],
    replicas: AvailabilityReplica[],
    listeners: AvailabilityListener[]
}

const prettyMode = (mode: string) => mode === 'SYNCHRONOUS_COMMIT' ? 'synchronous' : 'asynchronous';

function AvailabilityGroupsTable(props: AvailabilityGroupsTableProps) {
    const { clusterId, groups, replicas, listeners } = props;
    const timeRangeContext = useContext(TimeRangeContext);

    return (
        <div className='table-responsive'>
            <table className="table">
                <thead>
                <tr>
                    <th>Name</th>
                    <th>Health</th>
                    <th>Replicas</th>
                    <th>Listener</th>
                    <th>Failover</th>
                    <th>Sync state</th>
                </tr>
                </thead>
                <tbody>
                {groups.map(group => {
                    const groupReplicas = replicas.filter(replica => replica.groupid === group.id);
                    const listener = listeners.find(item => item.groupid === group.id);
                    return (
                        <tr key={group.id}>
                            <td className="fw-bold">
                                <i className="fal fa-layer-group fa-fw me-1" aria-hidden="true"/>
                                <Link to={`/clusters/${clusterId}/availability-groups/${group.id}?${buildRedirectQuery(timeRangeContext.timeRange)}`}>
                                    {group.name}
                                </Link>
                            </td>
                            <td><HealthBadge value={group.health}/></td>
                            <td>
                                {groupReplicas.map(replica => (
                                    <div key={replica.id} className={replica.role === 'PRIMARY' ? 'fw-bold' : ''}>
                                        {replica.servername}
                                        <span className="text-muted fw-normal">
                                            {replica.role === 'PRIMARY' ? ' (primary)' : ` (${prettyMode(replica.availabilitymode)})`}
                                        </span>
                                    </div>
                                ))}
                            </td>
                            <td>
                                {listener ? (
                                    <React.Fragment>
                                        <div>{listener.dnsname} <HealthBadge value={listener.state}/></div>
                                        <div className="text-muted">({listener.ipaddresses.join(', ')})</div>
                                    </React.Fragment>
                                ) : (
                                    <span className="text-muted">None</span>
                                )}
                            </td>
                            <td>
                                {group.failovermode === 'AUTOMATIC' ? 'Automatic' : 'Manual'}
                                {group.failoverready === false &&
                                    <i className="fal fa-exclamation-triangle fa-fw amber-icon ms-1"
                                       data-tooltip-id="global-tooltip"
                                       data-tooltip-content="Not ready for automatic failover"
                                       aria-hidden="true"/>}
                            </td>
                            <td><HealthBadge value={group.syncstate}/></td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
}

export default AvailabilityGroupsTable;
